import { useState } from "react";
import { FileText, Download, Plus, Trash2, ChevronDown, ChevronUp, CheckCircle2, Star, Users } from "lucide-react";
import { supabase } from "./supabaseClient";

const MEMOS_KEY = "binos_investment_memos";

function getSavedMemos() {
  try { return JSON.parse(localStorage.getItem(MEMOS_KEY) || "[]"); } catch { return []; }
}
function saveAllMemos(memos) {
  try { localStorage.setItem(MEMOS_KEY, JSON.stringify(memos)); } catch {}
}

const VERDICT_OPTIONS = [
  { value: "Strong Invest", color: "#16a34a", bg: "rgba(22,163,74,0.1)", border: "rgba(22,163,74,0.3)" },
  { value: "Invest", color: "#2563eb", bg: "rgba(37,99,235,0.1)", border: "rgba(37,99,235,0.3)" },
  { value: "Watch", color: "#d97706", bg: "rgba(217,119,6,0.1)", border: "rgba(217,119,6,0.3)" },
  { value: "Pass", color: "#dc2626", bg: "rgba(220,38,38,0.1)", border: "rgba(220,38,38,0.3)" },
];

const SECTIONS = [
  { key: "companyOverview", label: "Company Overview", placeholder: "What does the company do? What problem does it solve? Who are the founders?" },
  { key: "investmentThesis", label: "Investment Thesis", placeholder: "Why are we interested in this deal? What makes this company compelling from our fund's perspective?" },
  { key: "marketOpportunity", label: "Market Opportunity", placeholder: "How large is the market? What's the TAM/SAM/SOM? Is the market growing?" },
  { key: "productAndTechnology", label: "Product & Technology", placeholder: "What does the product do? Is there a technology moat? What's the differentiation?" },
  { key: "teamAssessment", label: "Team Assessment", placeholder: "Who are the founders? What's their background? Do they have relevant experience? Any gaps?" },
  { key: "tractionAndMetrics", label: "Traction & Metrics", placeholder: "ARR, MRR, growth rate, NRR, burn, runway. Key milestones achieved." },
  { key: "financialAnalysis", label: "Financial Analysis", placeholder: "Valuation, cap table, use of funds, path to profitability." },
  { key: "competitiveLandscape", label: "Competitive Landscape", placeholder: "Who are the main competitors? What's the moat? Why will this company win?" },
  { key: "risksAndMitigants", label: "Risks & Mitigants", placeholder: "What are the top 3-5 risks? How does the company plan to mitigate them?" },
  { key: "dealTerms", label: "Deal Terms", placeholder: "Investment amount, valuation, instrument (SAFE/equity), pro-rata rights, board seat." },
  { key: "nextSteps", label: "Next Steps", placeholder: "What needs to happen before we commit? Due diligence items, follow-up calls, references." },
];

function SectionEditor({ label, placeholder, value, onChange }) {
  const [isOpen, setIsOpen] = useState(true);
  const wordCount = value ? value.trim().split(/\s+/).filter(Boolean).length : 0;

  return (
    <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden", marginBottom: 10 }}>
      <button onClick={() => setIsOpen(!isOpen)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: value ? "#16a34a" : "rgba(255,255,255,0.15)", flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{label}</span>
          {value && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontWeight: 500 }}>{wordCount} words</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {value && <CheckCircle2 style={{ width: 14, height: 14, color: "#16a34a" }} />}
          {isOpen ? <ChevronUp style={{ width: 14, height: 14, color: "rgba(255,255,255,0.3)" }} /> : <ChevronDown style={{ width: 14, height: 14, color: "rgba(255,255,255,0.3)" }} />}
        </div>
      </button>

      {isOpen && (
        <div style={{ padding: "0 18px 16px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            rows={5}
            style={{ width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, outline: "none", fontSize: 13, fontWeight: 400, color: "#fff", resize: "vertical", fontFamily: "inherit", lineHeight: 1.7, boxSizing: "border-box", marginTop: 12 }}
            onFocus={e => e.target.style.borderColor = "#dc2626"}
            onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"}
          />
          {value && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button onClick={() => onChange("")}
                style={{ padding: "7px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.3)", cursor: "pointer", fontFamily: "inherit" }}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MemoCard({ memo, isSelected, onClick, onDelete }) {
 
  const completedSections = SECTIONS.filter(s => memo.sections?.[s.key]).length;

  return (
    <div onClick={onClick}
      style={{ background: isSelected ? "rgba(220,38,38,0.08)" : "#1a1a1a", border: isSelected ? "1px solid rgba(220,38,38,0.4)" : "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 16px", cursor: "pointer", transition: "all 0.15s" }}
      onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}}
      onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "#1a1a1a"; }}}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: "0 0 3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.2px" }}>{memo.companyName || "Untitled Memo"}</p>
          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0, fontWeight: 500 }}>{new Date(memo.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
        </div>
        <button onClick={e => { e.stopPropagation(); onDelete(memo.id); }}
          style={{ padding: 4, background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.2)", flexShrink: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
          onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.2)"}>
          <Trash2 style={{ width: 13, height: 13 }} />
        </button>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 100, background: (VERDICT_OPTIONS.find(v => v.value === memo.verdict) || VERDICT_OPTIONS[2]).bg, color: (VERDICT_OPTIONS.find(v => v.value === memo.verdict) || VERDICT_OPTIONS[2]).color, border: `1px solid ${(VERDICT_OPTIONS.find(v => v.value === memo.verdict) || VERDICT_OPTIONS[2]).border}` }}>{memo.verdict || "Draft"}</span>
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontWeight: 500 }}>{completedSections}/{SECTIONS.length} sections</span>
      </div>
    </div>
  );
}

export default function InvestmentMemo() {
  const [memos, setMemos] = useState(getSavedMemos());
  const [selectedMemoId, setSelectedMemoId] = useState(memos[0]?.id || null);

  const selectedMemo = memos.find(m => m.id === selectedMemoId) || null;

  const createNewMemo = () => {
    const memo = {
      id: Date.now(),
      companyName: "",
      analyst: "",
      date: new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }),
      verdict: "Watch",
      starred: false,
      sections: {},
      createdAt: new Date().toISOString(),
    };
    const updated = [memo, ...memos];
    setMemos(updated);
    saveAllMemos(updated);
    setSelectedMemoId(memo.id);
  };

  const updateMemo = (field, value) => {
    const updated = memos.map(m => m.id === selectedMemoId ? { ...m, [field]: value } : m);
    setMemos(updated);
    saveAllMemos(updated);
  };

  const updateSection = (sectionKey, value) => {
    const updated = memos.map(m => m.id === selectedMemoId ? { ...m, sections: { ...m.sections, [sectionKey]: value } } : m);
    setMemos(updated);
    saveAllMemos(updated);
  };

  const deleteMemo = (id) => {
    const updated = memos.filter(m => m.id !== id);
    setMemos(updated);
    saveAllMemos(updated);
    if (selectedMemoId === id) setSelectedMemoId(updated[0]?.id || null);
  };

  const downloadMemo = () => {
    if (!selectedMemo) return;
    let content = `INVESTMENT MEMO\n${"=".repeat(60)}\n\n`;
    content += `Company: ${selectedMemo.companyName}\n`;
    content += `Analyst: ${selectedMemo.analyst || "Not specified"}\n`;
    content += `Date: ${selectedMemo.date}\n`;
    content += `Verdict: ${selectedMemo.verdict}\n\n`;
    content += `${"=".repeat(60)}\n\n`;
    SECTIONS.forEach(section => {
      if (selectedMemo.sections?.[section.key]) {
        content += `${section.label.toUpperCase()}\n${"-".repeat(40)}\n`;
        content += `${selectedMemo.sections[section.key]}\n\n`;
      }
    });
    content += `\n${"=".repeat(60)}\n`;
    content += `Generated by Binos · binosai.com\n`;
    content += `Confidential — For internal use only\n`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedMemo.companyName || "investment-memo"}-memo.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const shareWithTeam = async () => {
    if (!selectedMemo) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { showToast("Not signed in"); return; }

      const { data: workspaces } = await supabase
        .from("workspaces")
        .select("id")
        .or(`created_by.eq.${session.user.id},members.cs.{"${session.user.id}"}`)
        .limit(1);

      const workspace_id = workspaces?.[0]?.id;
      if (!workspace_id) { showToast("No workspace found"); return; }

      const entity_id = crypto.randomUUID();

      await supabase.from("activity_feed").insert({
        workspace_id,
        user_id: session.user.id,
        action: "shared_memo",
        entity_type: "memo",
        entity_name: selectedMemo.companyName || "Investment Memo",
        metadata: { memo_content: selectedMemo, verdict: selectedMemo.verdict },
      });

      await supabase.from("comments").insert({
        workspace_id,
        user_id: session.user.id,
        entity_type: "memo",
        entity_id,
        entity_name: selectedMemo.companyName,
        content: "Shared investment memo — " + selectedMemo.verdict,
      });

      showToast("Memo shared with your team");
    } catch {
      showToast("Failed to share memo");
    }
  };

  const completedSections = selectedMemo ? SECTIONS.filter(s => selectedMemo.sections?.[s.key]).length : 0;
  const progress = selectedMemo ? Math.round((completedSections / SECTIONS.length) * 100) : 0;

  return (
    <div style={{ display: "flex", gap: 16, minHeight: "calc(100vh - 128px)", fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* LEFT — Memo list */}
      <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>

        <button onClick={createNewMemo}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px", background: "#dc2626", border: "none", borderRadius: 12, fontSize: 13, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit", transition: "opacity 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
          <Plus style={{ width: 16, height: 16 }} /> New Memo
        </button>

        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          {memos.length === 0 && (
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "40px 20px", textAlign: "center" }}>
              <FileText style={{ width: 28, height: 28, color: "rgba(255,255,255,0.1)", margin: "0 auto 10px" }} />
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", margin: 0, fontWeight: 500 }}>No memos yet. Create your first one.</p>
            </div>
          )}
          {memos.map(memo => (
            <MemoCard key={memo.id} memo={memo} isSelected={selectedMemoId === memo.id} onClick={() => setSelectedMemoId(memo.id)} onDelete={deleteMemo} />
          ))}
        </div>
      </div>

      {/* RIGHT — Memo editor */}
      {selectedMemo ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 0, overflow: "hidden" }}>

          {/* Header */}
          <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "20px 24px", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <input
                  value={selectedMemo.companyName}
                  onChange={e => updateMemo("companyName", e.target.value)}
                  placeholder="Company Name"
                  style={{ width: "100%", background: "none", border: "none", outline: "none", fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: "-0.5px", fontFamily: "inherit", padding: 0 }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                  <input
                    value={selectedMemo.analyst}
                    onChange={e => updateMemo("analyst", e.target.value)}
                    placeholder="Analyst name"
                    style={{ background: "none", border: "none", outline: "none", fontSize: 12, color: "rgba(255,255,255,0.4)", fontFamily: "inherit", padding: 0, width: 140 }}
                  />
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.2)" }}>·</span>
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{selectedMemo.date}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button onClick={() => updateMemo("starred", !selectedMemo.starred)}
                  style={{ padding: "8px 10px", background: selectedMemo.starred ? "rgba(234,179,8,0.1)" : "rgba(255,255,255,0.05)", border: selectedMemo.starred ? "1px solid rgba(234,179,8,0.3)" : "1px solid rgba(255,255,255,0.08)", borderRadius: 9, cursor: "pointer" }}>
                  <Star style={{ width: 14, height: 14, color: selectedMemo.starred ? "#eab308" : "rgba(255,255,255,0.3)" }} fill={selectedMemo.starred ? "#eab308" : "none"} />
                </button>
                <button onClick={downloadMemo}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.5)", cursor: "pointer", fontFamily: "inherit" }}>
                  <Download style={{ width: 12, height: 12 }} /> Download
                </button>
                <button onClick={shareWithTeam}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#2563eb", border: "1px solid rgba(37,99,235,0.6)", borderRadius: 9, fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
                  <Users style={{ width: 12, height: 12 }} /> Share with Team
                </button>
              </div>
            </div>

            {/* Verdict + Progress */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {VERDICT_OPTIONS.map(v => (
                  <button key={v.value} onClick={() => updateMemo("verdict", v.value)}
                    style={{ padding: "5px 12px", borderRadius: 100, fontSize: 11, fontWeight: 700, border: `1px solid ${selectedMemo.verdict === v.value ? v.border : "rgba(255,255,255,0.08)"}`, background: selectedMemo.verdict === v.value ? v.bg : "transparent", color: selectedMemo.verdict === v.value ? v.color : "rgba(255,255,255,0.3)", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                    {v.value}
                  </button>
                ))}
              </div>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 100, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${progress}%`, background: progress === 100 ? "#16a34a" : "#dc2626", borderRadius: 100, transition: "width 0.5s ease" }} />
                </div>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontWeight: 600, whiteSpace: "nowrap" }}>{completedSections}/{SECTIONS.length} sections</span>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {SECTIONS.map(section => (
              <SectionEditor
                key={section.key}
                label={section.label}
                placeholder={section.placeholder}
                value={selectedMemo.sections?.[section.key] || ""}
                onChange={value => updateSection(section.key, value)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, background: "#161616", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.03)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FileText style={{ width: 24, height: 24, color: "rgba(255,255,255,0.1)" }} />
          </div>
          <p style={{ fontSize: 15, fontWeight: 800, color: "rgba(255,255,255,0.2)", margin: 0, letterSpacing: "-0.3px" }}>No memo selected</p>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.12)", fontWeight: 500, margin: "0 0 16px" }}>Create a new memo to get started</p>
          <button onClick={createNewMemo}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
            <Plus style={{ width: 14, height: 14 }} /> New Memo
          </button>
        </div>
      )}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600, color: "#fff", zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.5)" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
